import React, { useState } from 'react';
import {
  FormGroup,
  Input,
  Button,
  Form,
  Card
} from 'reactstrap';
import { useParams, useHistory } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from 'react-query'
import clsx from 'clsx';

import AuthLayout from 'layouts/AuthLayout';
import { fetcher } from 'api/client';
import { recoverPasssword, respondChallenge } from 'api/actions/auth';
import { toast } from 'components/Shared/Toast';

const PasswordChangeSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'ShortPassword')
    .matches(/[a-z]/, 'MissingLowerCase')
    .matches(/[A-Z]/, 'MissingUpperCase')
    .matches(/[0-9]/, 'MissingDigit')
    .required('Required'),
  confirmPassword: Yup.string()
    .min(8, 'ShortPassword')
    .matches(/[a-z]/, 'MissingLowerCase')
    .matches(/[A-Z]/, 'MissingUpperCase')
    .matches(/[0-9]/, 'MissingDigit')
    .required('Required'),
});


function ChangePasswordForm() {
  const { t } = useTranslation();
  const { username, code } = useParams();
  const history = useHistory();
  const { register, errors, control, watch, handleSubmit } = useForm({
    resolver: yupResolver(PasswordChangeSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });
  const password = useWatch({
    name: 'password',
    control,
    defaultValue: ''
  });
  const confirmPassword = useWatch({
    name: 'confirmPassword',
    control,
    defaultValue: ''
  });

  const onSuccess = ({ data }) => {
    const AuthenticationResult = data.AuthenticationResult ?? {};
    if (AuthenticationResult.AccessToken) {
      localStorage.setItem('tokenType', AuthenticationResult.TokenType);
      localStorage.setItem('jwt', AuthenticationResult.AccessToken);
      localStorage.setItem('id_token', AuthenticationResult.IdToken);
      localStorage.setItem('refreshToken', AuthenticationResult.RefreshToken);
      localStorage.setItem('expiresAt', new Date().getTime() + (AuthenticationResult.ExpiresIn * 1000));
      history.push('/dashboard');
    }
  }
  const onError = (error) => {
    toast.error(t(`errors.${error.message}`))
  }

  const { mutate: recoverPasswordMutate, isLoading: isRecoveringPassword } = useMutation((payload) => fetcher(recoverPasssword(payload)), {
    onSuccess,
    onError
  })
  const { mutate: respondChallengeMutate, isLoading: isRespondingToChallenge } = useMutation((payload) => fetcher(respondChallenge(payload)), {
    onSuccess,
    onError
  })

  const handleChange = (data) => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasTempPwd = urlParams.get('hasTmpPwd') === 'true';
    data.username = username

    if (hasTempPwd) {
      data.session = code;
      data.type = 'NEW_PASSWORD_REQUIRED';
      respondChallengeMutate(data)
    } else {
      data.code = code;
      recoverPasswordMutate(data)
    }

  }

  return (

    <Card className="p-4 mb-3 rounded-lg card-shadow">
      <span className="mb-3 font-weight-normal text-center">{t('login.password.change')}</span>
      <Form>
        <FormGroup>
          <Input autoFocus required type="password" name="password"
            placeholder={t('login.password.change_placeholder')} innerRef={register} />
        </FormGroup>
        <FormGroup>
          <Input required type="password" name="confirmPassword"
            placeholder={t('login.password.change_confirm_placeholder')} innerRef={register} />
        </FormGroup>
        <FormGroup className="d-flex flex-column">
          <PasswordRuleItem valid={password.length > 7} label={t('profile.security.min_chars')} />
          <PasswordRuleItem valid={password.match(/[0-9]/g)} label={t('profile.security.min_digit')} />
          <PasswordRuleItem valid={password.match(/[A-Z]/g)} label={t('profile.security.min_upper')} />
          <PasswordRuleItem valid={password.length > 0 && password === confirmPassword} label={t('profile.security.is_equals')} />
        </FormGroup>
        <Button color="primary" block onClick={handleSubmit(handleChange)} disabled={isRecoveringPassword || isRespondingToChallenge}>{t('login.password.change_save')}</Button>
      </Form>
    </Card>
  )
}

function PasswordRuleItem({ valid, label }) {
  return (
    <div className="mb-1">
      <i className={clsx('fas fa-circle', valid ? 'text-success' : 'text-muted')} /> {label}
    </div>
  )
}

export default function ChangePassword() {
  return (
    <AuthLayout>
      <ChangePasswordForm />
    </AuthLayout>
  )
}